// Copyright © SRGTech (Software Resonance Group Inc.). All rights reserved.
// SPDX-License-Identifier: LicenseRef-SRGTech-Proprietary
// See LICENSE and docs/legal/PROPRIETARY_COPYRIGHT.md.

/**
 * TanStack Query cache keys.
 *
 * Every key is prefixed with the tenant identifier the session reported. A
 * cache is a derivative that inherits the scope of its source
 * (`SEC2-016`, `DAT-006`), so two tenants can never collide in one cache and a
 * tenant switch cannot serve the previous tenant's rows.
 */

import type { ListQuery } from './ports';

/** Serialisable form of a list query used inside a cache key. */
function queryFingerprint(query: ListQuery | undefined): Readonly<Record<string, unknown>> {
  return {
    page: query?.page ?? 1,
    pageSize: query?.pageSize ?? 25,
    search: query?.search ?? '',
  };
}

/** Placeholder scope used before a session has been resolved. */
export const UNRESOLVED_TENANT_SCOPE = 'tenant:unresolved';

/**
 * Build the cache scope for a tenant.
 *
 * @param tenantId - Tenant identifier from the verified session, if known.
 * @returns A stable scope segment.
 */
export function tenantScope(tenantId: string | null | undefined): string {
  return tenantId === null || tenantId === undefined || tenantId === ''
    ? UNRESOLVED_TENANT_SCOPE
    : `tenant:${tenantId}`;
}

/** Cache keys for every administration query. */
export const queryKeys = {
  session: () => ['session'] as const,
  controlPlane: {
    summary: (scope: string) => [scope, 'control-plane', 'summary'] as const,
    tenants: (scope: string, query?: ListQuery) =>
      [scope, 'control-plane', 'tenants', queryFingerprint(query)] as const,
    // The v2 college overview (**ADR-0076**). A key of its own rather than a
    // reuse of `tenants`: the two reads answer different documents from the
    // same route, and sharing a key would let a v1 row satisfy a v2 read from
    // cache.
    collegeOverview: (scope: string, query?: ListQuery) =>
      [scope, 'control-plane', 'college-overview', queryFingerprint(query)] as const,
    // One key per college, so an onboarding detail read for one college is
    // never served from another's cache entry.
    collegeOnboarding: (scope: string, tenantId: string) =>
      [scope, 'control-plane', 'college-onboarding', tenantId] as const,
    aiServices: (scope: string, query?: ListQuery) =>
      [scope, 'control-plane', 'ai-services', queryFingerprint(query)] as const,
    knowledgeCollections: (scope: string, query?: ListQuery) =>
      [scope, 'control-plane', 'knowledge-collections', queryFingerprint(query)] as const,
    documents: (scope: string, query?: ListQuery) =>
      [scope, 'control-plane', 'documents', queryFingerprint(query)] as const,
    policies: (scope: string, query?: ListQuery) =>
      [scope, 'control-plane', 'policies', queryFingerprint(query)] as const,
    approvals: (scope: string, query?: ListQuery) =>
      [scope, 'control-plane', 'approvals', queryFingerprint(query)] as const,
    metrics: (scope: string, query?: ListQuery) =>
      [scope, 'control-plane', 'metrics', queryFingerprint(query)] as const,
    auditEvents: (scope: string, query?: ListQuery) =>
      [scope, 'control-plane', 'audit-events', queryFingerprint(query)] as const,
    monitoredServices: (scope: string, query?: ListQuery) =>
      [scope, 'control-plane', 'monitored-services', queryFingerprint(query)] as const,
    roles: (scope: string, query?: ListQuery) =>
      [scope, 'control-plane', 'roles', queryFingerprint(query)] as const,
    accessGrants: (scope: string, query?: ListQuery) =>
      [scope, 'control-plane', 'access-grants', queryFingerprint(query)] as const,
    memoryRecords: (scope: string, query?: ListQuery) =>
      [scope, 'control-plane', 'memory-records', queryFingerprint(query)] as const,
  },
  tenant: {
    summary: (scope: string) => [scope, 'tenant', 'summary'] as const,
    connectors: (scope: string, query?: ListQuery) =>
      [scope, 'tenant', 'connectors', queryFingerprint(query)] as const,
    dataSources: (scope: string, query?: ListQuery) =>
      [scope, 'tenant', 'data-sources', queryFingerprint(query)] as const,
    schemaMappings: (scope: string, query?: ListQuery) =>
      [scope, 'tenant', 'schema-mappings', queryFingerprint(query)] as const,
    documents: (scope: string, query?: ListQuery) =>
      [scope, 'tenant', 'documents', queryFingerprint(query)] as const,
    knowledgeCollections: (scope: string, query?: ListQuery) =>
      [scope, 'tenant', 'knowledge-collections', queryFingerprint(query)] as const,
    policies: (scope: string, query?: ListQuery) =>
      [scope, 'tenant', 'policies', queryFingerprint(query)] as const,
    auditEvents: (scope: string, query?: ListQuery) =>
      [scope, 'tenant', 'audit-events', queryFingerprint(query)] as const,
    healthChecks: (scope: string, query?: ListQuery) =>
      [scope, 'tenant', 'health-checks', queryFingerprint(query)] as const,
  },
} as const;
